// components/DonationModal.js
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "./DonationModal.css";
import "../containers/ModalTheme.css";
import { getDonationTotalForYear, postDonationSession } from "../services/AnimalSplitCore"; // use your API function
import { useTranslation } from "react-i18next";
import AnimalEasterEggBadge from "./AnimalEasterEggBadge";

const GOAL_USD = 550;
const GOAL_CENTS = GOAL_USD * 100;

export default function DonationModal({ onClose }) {
    const { t } = useTranslation();
    const [amount, setAmount] = useState(100); // default $1
    const [alias, setAlias] = useState("");
    const [note, setNote] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [totalCents, setTotalCents] = useState(null);
    const [isLoadingTotal, setIsLoadingTotal] = useState(false);
    const campaignYear = new Date().getFullYear();

    const mascotSeed = useMemo(() => {
        return `donate:${window.location.pathname || "animalsplit"}`;
    }, []);

    const handleSubmit = async () => {
        if (isSubmitting) return;
        try {
            setIsSubmitting(true);
            const session = await postDonationSession({
                amount,
                alias: alias?.trim() || "",      // always string
                note: note?.trim() || "",        // always string
                returnUrl: window.location.href
            });
            if (!session || !session.url) {
                throw new Error("Invalid session response");
            }
            try {
                localStorage.setItem('hasDonated', 'true');
                const today = new Date().toISOString().slice(0, 10);
                localStorage.setItem('lastReturnDonateShown', today);
            } catch (e) {
                // ignore localStorage failures in private mode
            }
            window.location.href = session.url;
        } catch (error) {
            console.error("Payment failed", error);
            alert(t("donation.payment_failed"));
            setIsSubmitting(false);
        }
    };

    // Prevent background scroll while the modal is open (matches other overlays in the app)
    useEffect(() => {
        const prevOverflow = document.body.style.overflow;
        const prevPaddingRight = document.body.style.paddingRight;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = "hidden";
        if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
        return () => {
            document.body.style.overflow = prevOverflow;
            document.body.style.paddingRight = prevPaddingRight;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setIsLoadingTotal(true);
                const res = await getDonationTotalForYear(campaignYear);
                if (cancelled) return;
                setTotalCents(Number(res?.totalCents ?? 0));
            } catch (e) {
                if (!cancelled) setTotalCents(null);
            } finally {
                if (!cancelled) setIsLoadingTotal(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [campaignYear]);

    const clampPct = (pct) => Math.max(0, Math.min(100, pct));
    const hasTotal = Number.isFinite(Number(totalCents));
    const progressPct = clampPct(
        Math.round(((hasTotal ? Number(totalCents) : 0) / GOAL_CENTS) * 100),
    );

    const formatUsd = (cents) => {
        const v = (Number(cents ?? 0) || 0) / 100;
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: v % 1 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
        }).format(v);
    };

    const modal = (
        <div
            className="donation-modal-backdrop"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose?.();
            }}
        >
            <div className="donation-modal as-modal as-theme" onMouseDown={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div className="as-modal-header-row">
                        <div className="as-modal-title-row">
                            <span className="as-modal-icon-badge" aria-hidden="true">💝</span>
                            <div className="donation-title-stack">
                                <h5 className="modal-title">{t("donation.title")}</h5>
                                <div className="small text-muted">{t("donation.subtitle")}</div>
                            </div>
                        </div>
                        <div className="as-modal-actions">
                            <AnimalEasterEggBadge
                              seed={mascotSeed}
                              className="donation-mascot"
                              ariaLabel="Donation mascot easter egg"
                            />
                            <button type="button" className="btn-close" onClick={onClose} aria-label={t("general.close")} />
                        </div>
                    </div>
                </div>

                <div className="modal-body">
                    <div className="donation-progress">
                        <div className="donation-progress-row">
                            <div className="donation-progress-label">
                                {t("donation.progress_label", { year: campaignYear })}
                            </div>
                            <div className="donation-progress-value">
                                {isLoadingTotal
                                    ? t("donation.loading_total")
                                    : hasTotal
                                        ? `${formatUsd(totalCents)} / ${formatUsd(GOAL_CENTS)}`
                                        : t("donation.total_unavailable")}
                            </div>
                        </div>
                        <div className="donation-progress-track">
                            <div
                                className="donation-progress-fill"
                                style={{ width: `${Number.isFinite(progressPct) ? progressPct : 0}%` }}
                            />
                        </div>
                        <div className="donation-progress-meta">
                            {isLoadingTotal
                                ? "—"
                                : hasTotal
                                    ? t("donation.progress_meta", { pct: progressPct, goal: GOAL_USD })
                                    : t("donation.total_unavailable")}
                        </div>
                    </div>

                    <div className="donation-amount-options">
                        {[100, 500, 1000].map((val) => (
                            <button
                                type="button"
                                key={val}
                                className={`btn donation-amount-btn ${amount === val ? "as-btn-primary-gradient" : "as-btn-soft"}`}
                                onClick={() => setAmount(val)}
                                disabled={isSubmitting}
                            >
                                ${val / 100}
                            </button>
                        ))}
                    </div>

                    <div className="mt-3">
                        <label className="form-label donation-label">{t("donation.name_label")}</label>
                        <input
                            type="text"
                            className="form-control mb-2"
                            placeholder={t("donation.name_placeholder")}
                            value={alias}
                            onChange={(e) => setAlias(e.target.value)}
                            disabled={isSubmitting}
                        />
                        <label className="form-label donation-label">{t("donation.message_label")}</label>
                        <input
                            className="form-control"
                            placeholder={t("donation.message_placeholder")}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </div>
                </div>

                <div className="modal-footer as-modal-footer">
                    <button type="button" className="btn as-btn-soft" onClick={onClose} disabled={isSubmitting}>
                        {t("general.close")}
                    </button>
                    <button
                        type="button"
                        className="btn as-btn-primary-gradient"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? t("donation.redirecting") : t("donation.proceed_to_payment")}
                    </button>
                </div>
            </div>
        </div>
    );

    // Render into document.body so it's not affected by parent stacking contexts/transforms.
    return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
}
